import { Component, Input } from '@angular/core';

import { AccountService } from '../../services/account/account.service';
import { LocationService } from '../../services/location/location.service';
import { MyAccount } from '../../models/my-account';
import { propExists } from '../../utils';
import { UserService } from '../../services/user/user.service';

enum LoginMode {
  emailAndPassword,
  otp,
  selectLocation,
  resetPassword,
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent {

  @Input() supportPinMode: boolean;
  @Input() permissionCheck: (account: MyAccount) => string;
  @Input() siteName: string;

  email = '';
  password = '';
  otpToken = '';
  usePinMode = false;

  inPasswordResetMode = false;
  passwordResetSuccess = false;

  selectedLocationId: number = null;

  errorMessage = '';
  loadingLogin = false;
  loadingOtp = false;
  loadingSelectLocation = false;
  loadingPasswordReset = false;

  public readonly LoginMode = LoginMode;

  constructor(
    public accountService: AccountService,
    public userService: UserService,
    public locationService: LocationService
  ) {}

  /**
   * Handle a login form submission.
   */
  onSubmit() {
    switch (this.getLoginMode()) {
      case LoginMode.emailAndPassword:
        this.onLoginSubmit();
        break;
      case LoginMode.otp:
        this.onOtpSubmit();
        break;
      case LoginMode.selectLocation:
        this.onLocationSubmit();
        break;
      case LoginMode.resetPassword:
        this.onPasswordResetSubmit();
        break;
    }
  }

  loadingSelectLocationData() {
    return !this.accountService.myAccountInfoIsLoaded() || !this.locationService.getLocations();
  }

  public isLoadingSubmit() {
    return (
      this.loadingLogin ||
      this.loadingOtp ||
      this.loadingSelectLocation ||
      this.loadingPasswordReset
    );
  }

  public enterPasswordResetMode() {
    if (this.isLoadingSubmit()) { return; }

    this.inPasswordResetMode = true;
    this.errorMessage = '';
  }

  public exitPasswordResetMode() {
    if (this.isLoadingSubmit()) { return; }

    this.inPasswordResetMode = false;
    this.passwordResetSuccess = false;
    this.errorMessage = '';
  }

  public getLoginMode(): LoginMode {
    if (this.accountService.getSelectLocationInProgress()) {
      return LoginMode.selectLocation;
    } else if (this.accountService.getOtpInProgress()) {
      return LoginMode.otp;
    } else if (this.inPasswordResetMode) {
      return LoginMode.resetPassword;
    } else {
      return LoginMode.emailAndPassword;
    }
  }

  private onLoginSubmit() {
    this.errorMessage = '';
    this.loadingLogin = true;

    this.accountService
      .login(
        this.email,
        this.password,
        this.usePinMode,
        this.supportPinMode
      )
      .then(() => {
        const myAccount = this.accountService.getMyAccount();

        if (propExists(this.permissionCheck)) {
          this.errorMessage = this.permissionCheck(myAccount);
          if (this.errorMessage !== '') {
            this.accountService.logout();
            return;
          }
        }

        this.email = '';
        this.password = '';

        this.selectedLocationId = myAccount.default_gym_id;
      })
      .catch(reason => {
        if (reason.status === 403 && reason.error?.useful_error_message) {
          // The server supplies the text for a closed organization.
          this.errorMessage = reason.error.useful_error_message;
        } else if (reason.status === 400) {
          if (reason.error === 'pin_mode_required') {
            if (this.supportPinMode) {
              this.errorMessage = '"Require PINs when making changes" must be selected when signing in with this account.';
            } else {
              this.errorMessage = 'This account requires PIN Mode, but the Check-In and Check-Out sites do not allow PIN Mode. Please log in using an account which does not have the "PIN Mode Required?" setting.'
            }
          } else {
            this.errorMessage = 'Invalid email/password combination.';
          }
        } else {
          this.errorMessage = 'Server error.';
        }
      })
      .finally(() => { this.loadingLogin = false; });
  }

  private onOtpSubmit() {
    this.errorMessage = '';
    this.loadingOtp = true;

    this.accountService
      .loginOtp(this.otpToken)
      .then(() => {
        // If the page was refreshed between the initial login and the OTP
        // submit, the default location ID must be obtained again.
        const myAccount = this.accountService.getMyAccount();
        this.selectedLocationId = myAccount.default_gym_id;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.errorMessage = 'Server error.';
        }
      })
      .finally(() => {
        this.otpToken = '';
        this.loadingOtp = false;
      });
  }

  private onLocationSubmit() {
    this.errorMessage = '';
    this.loadingSelectLocation = true;

    this.accountService
      .setDefaultLocation(this.selectedLocationId)
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = 'Invalid location.';
        } else {
          this.errorMessage = 'Server error.';
        }
      })
      .finally(() => { this.loadingSelectLocation = false; });
  }

  private onPasswordResetSubmit() {
    this.errorMessage = '';
    this.loadingPasswordReset = true;

    this.userService
      .sendPasswordResetEmail(this.email)
      .then(() => { this.passwordResetSuccess = true; })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.errorMessage = 'Server error.';
        }
      })
      .finally(() => { this.loadingPasswordReset = false; });
  }

}
